import type { LucideIcon } from 'lucide-react';
import type { RecursoPlano } from '@/lib/planos';
import {
  BarChart3,
  Bot,
  Bell,
  Building2,
  Calendar,
  CalendarClock,
  Car,
  ClipboardCheck,
  ClipboardList,
  FilePlus,
  Gift,
  Import,
  LayoutDashboard,
  Megaphone,
  Package,
  Settings,
  ShoppingCart,
  Store,
  Truck,
  UserCog,
  Users,
  Wallet,
  Warehouse,
  Landmark,
  History,
  SlidersHorizontal,
} from 'lucide-react';

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Match exato de rota (evita que `/pdv` fique ativo em `/pdv/balcao`). */
  end?: boolean;
  /** Se definido, o item só aparece para quem tiver pelo menos um destes perfis. */
  anyPerfil?: string[];
  /** Quick 260820-dqz: se definido, o item só aparece quando este recurso do plano estiver ligado na organização. */
  recursoExigido?: RecursoPlano;
};

export type NavSection = { title: string; items: NavItem[] };

const PERFIS_SEM_CAIXA = [
  'ADMIN',
  'GERENTE_FILIAL',
  'GERENTE_REGIONAL',
  'MECANICO',
  'ATENDENTE',
];

const PERFIS_PDV = ['ADMIN', 'GERENTE_FILIAL', 'GERENTE_REGIONAL', 'CAIXA'];

export const navSections: NavSection[] = [
  {
    title: 'Cadastros',
    items: [
      { to: '/clientes', label: 'Clientes', icon: Users, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/veiculos', label: 'Veículos', icon: Car, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/produtos', label: 'Produtos', icon: Package, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/fornecedores', label: 'Fornecedores', icon: Building2, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/usuarios', label: 'Usuários', icon: UserCog, anyPerfil: ['ADMIN', 'GERENTE_FILIAL'] },
    ],
  },
  {
    title: 'Estoque',
    items: [
      { to: '/estoque', label: 'Estoque por filial', icon: Warehouse, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/inventario', label: 'Inventário cíclico', icon: ClipboardCheck, end: true, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/entradas-compra', label: 'Entradas de compra', icon: Import, anyPerfil: PERFIS_SEM_CAIXA },
      {
        to: '/transferencias',
        label: 'Transferências',
        icon: Truck,
        anyPerfil: PERFIS_SEM_CAIXA,
        recursoExigido: 'transferenciaFiliais',
      },
    ],
  },
  {
    title: 'Vendas',
    items: [
      { to: '/pdv', label: 'PDV', icon: ShoppingCart, end: true, anyPerfil: PERFIS_PDV },
      { to: '/pdv/vendas', label: 'Vendas no caixa', icon: Store, anyPerfil: PERFIS_PDV },
      { to: '/os/listagem', label: 'Ordens de serviço', icon: ClipboardList, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/os/orcamentos', label: 'Orçamentos', icon: ClipboardCheck, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/os', label: 'Nova OS', icon: FilePlus, end: true, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/agendamentos', label: 'Agendamentos', icon: Calendar, anyPerfil: PERFIS_SEM_CAIXA },
    ],
  },
  {
    title: 'Gestão',
    items: [
      { to: '/painel', end: true, label: 'Painel', icon: LayoutDashboard, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/assistente', label: 'Assistente operacional', icon: Bot, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/financeiro', label: 'Financeiro', icon: Landmark, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/contador', label: 'Pacote do contador', icon: FilePlus, anyPerfil: ['ADMIN', 'GERENTE_FILIAL', 'GERENTE_REGIONAL'] },
      {
        to: '/relatorios',
        label: 'Relatórios',
        icon: BarChart3,
        anyPerfil: [...PERFIS_SEM_CAIXA, 'CONSULTOR'],
      },
    ],
  },
  {
    title: 'Administração',
    items: [
      { to: '/notificacoes', label: 'Notificações', icon: Bell, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/fidelidade', label: 'Fidelidade', icon: Gift, anyPerfil: PERFIS_SEM_CAIXA },
      { to: '/retornos', label: 'Retornos', icon: CalendarClock, anyPerfil: PERFIS_SEM_CAIXA },
      {
        to: '/campanhas',
        label: 'Campanhas',
        icon: Megaphone,
        // D-15: trio restrito de gestão — deliberadamente diferente de PERFIS_SEM_CAIXA
        // (que inclui MECANICO/ATENDENTE). O operador de caixa só vê o EFEITO da campanha
        // no PDV (selo de desconto), nunca o item de gestão.
        anyPerfil: ['ADMIN', 'GERENTE_FILIAL', 'GERENTE_REGIONAL'],
      },
      { to: '/caixa', label: 'Caixa', icon: Wallet, anyPerfil: PERFIS_SEM_CAIXA },
      {
        to: '/campos-customizados',
        label: 'Campos customizáveis',
        icon: SlidersHorizontal,
        anyPerfil: ['ADMIN', 'GERENTE_FILIAL', 'GERENTE_REGIONAL'],
      },
      {
        to: '/auditoria',
        label: 'Auditoria',
        icon: History,
        anyPerfil: ['ADMIN', 'GERENTE_FILIAL', 'GERENTE_REGIONAL'],
      },
      { to: '/configuracoes', label: 'Configurações', icon: Settings, anyPerfil: PERFIS_SEM_CAIXA },
    ],
  },
];
